import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, Truck, ChevronRight, MapPin, User, Package,
  Clock, CheckCircle, XCircle, ArrowRight, Copy, MessageSquare
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { pickupOrderAPI, driverAPI, unitAPI, supplierAPI } from '@/lib/api';
import { cn, formatDate, formatDateTime } from '@/lib/utils';
import { toast } from 'sonner';
import useAuthStore from '@/store/authStore';

const statusConfig = {
  created: { label: 'Created', color: 'bg-slate-100 text-slate-800', icon: Clock },
  assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-800', icon: User },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-800', icon: Truck },
  picked_up: { label: 'Picked Up', color: 'bg-purple-100 text-purple-800', icon: Package },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const statusFlow = ['created', 'assigned', 'in_progress', 'picked_up', 'delivered'];

// Generate text message for driver
const generateDriverMessage = (order) => {
  const vendorName = order.vendor?.name || 'Unknown Vendor';
  const vendorAddress = order.vendor ? `${order.vendor.address}, ${order.vendor.city}, ${order.vendor.state}` : '';
  const units = order.unit_numbers?.join(', ') || 'N/A';
  const parts = order.parts_description || 'See order details';
  
  return `PICKUP ORDER: ${order.pickup_number}

LOCATION:
${vendorName}
${vendorAddress}

UNITS: ${units}

PARTS: ${parts}${order.notes ? `

NOTES: ${order.notes}` : ''}`;
};

const CopyMessageButton = ({ order }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async (e) => {
    e.stopPropagation();
    const message = generateDriverMessage(order);
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success('Message copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={handleCopy}
      className="gap-1"
      data-testid={`copy-message-${order.id}`}
    >
      {copied ? (
        <>
          <CheckCircle className="w-4 h-4 text-green-500" />
          Copied!
        </>
      ) : (
        <>
          <MessageSquare className="w-4 h-4" />
          Copy for Driver
        </>
      )}
    </Button>
  );
};

const PickupOrderCard = ({ order, onStatusUpdate }) => {
  const navigate = useNavigate();
  const config = statusConfig[order.status] || statusConfig.created;
  const StatusIcon = config.icon;

  const currentIndex = statusFlow.indexOf(order.status);
  const nextStatus = currentIndex >= 0 && currentIndex < statusFlow.length - 1 
    ? statusFlow[currentIndex + 1] 
    : null;

  return (
    <Card 
      className="hover:shadow-md transition-shadow"
      data-testid={`pickup-card-${order.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-sm font-bold text-slate-900">
                {order.pickup_number}
              </span>
              <Badge className={cn("text-xs", config.color)}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {config.label}
              </Badge>
            </div>
            
            {/* Vendor info */}
            <div className="flex items-start gap-2 mb-2">
              <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-slate-900">{order.vendor?.name}</p>
                <p className="text-sm text-slate-500">
                  {order.vendor?.address}, {order.vendor?.city}, {order.vendor?.state}
                </p>
              </div>
            </div>

            {/* Driver & Units */}
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="flex items-center gap-1 text-slate-600">
                <User className="w-4 h-4" />
                {order.driver_name}
              </span>
              {order.unit_numbers?.length > 0 && (
                <span className="flex items-center gap-1 text-slate-600">
                  <Truck className="w-4 h-4" />
                  Units: {order.unit_numbers.join(', ')}
                </span>
              )}
            </div>

            {order.parts_description && (
              <p className="mt-2 text-sm text-slate-600 bg-slate-50 p-2 rounded">
                {order.parts_description}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col items-end gap-2">
            <p className="text-xs text-slate-500">{formatDate(order.created_at)}</p>
            <CopyMessageButton order={order} />
            {nextStatus && order.status !== 'cancelled' && (
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusUpdate(order, nextStatus);
                }}
                data-testid={`advance-status-${order.id}`}
              >
                <ArrowRight className="w-4 h-4 mr-1" />
                {statusConfig[nextStatus]?.label}
              </Button>
            )}
          </div>
        </div>

        {/* Status timestamps */}
        {(order.picked_up_at || order.delivered_at) && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex gap-4 text-xs text-slate-500">
            {order.picked_up_at && (
              <span>Picked up: {formatDateTime(order.picked_up_at)}</span>
            )}
            {order.delivered_at && (
              <span>Delivered: {formatDateTime(order.delivered_at)}</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const CreatePickupDialog = ({ onCreated }) => {
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [units, setUnits] = useState([]);
  const [newUnitNumber, setNewUnitNumber] = useState('');
  const [createdOrder, setCreatedOrder] = useState(null); // For showing message after creation
  const [formData, setFormData] = useState({
    pickup_number: '',
    vendor_id: '',
    driver_id: '',
    unit_ids: [],
    parts_description: '',
    notes: '',
  });

  useEffect(() => {
    if (open && !createdOrder) {
      Promise.all([
        supplierAPI.getAll(),
        driverAPI.getAll(),
        unitAPI.getAll(),
      ]).then(([vendorsRes, driversRes, unitsRes]) => {
        setVendors(vendorsRes.data.suppliers || []);
        setDrivers(driversRes.data.drivers || []);
        setUnits(unitsRes.data.units || []);
      });
    }
  }, [open, createdOrder]);

  const handleAddUnit = async () => {
    if (!newUnitNumber.trim()) return;
    
    try {
      const response = await unitAPI.create({ unit_number: newUnitNumber.trim() });
      const newUnit = response.data;
      setUnits(prev => [...prev, newUnit]);
      setFormData(prev => ({ ...prev, unit_ids: [...prev.unit_ids, newUnit.id] }));
      setNewUnitNumber('');
      toast.success(`Unit ${newUnitNumber} added`);
    } catch (error) {
      if (error.response?.status === 400) {
        // Unit exists, find and add it
        const existingUnit = units.find(u => u.unit_number === newUnitNumber.trim());
        if (existingUnit && !formData.unit_ids.includes(existingUnit.id)) {
          setFormData(prev => ({ ...prev, unit_ids: [...prev.unit_ids, existingUnit.id] }));
        }
        toast.error('Unit already exists');
      } else {
        toast.error('Failed to add unit');
      }
    }
  };

  const toggleUnit = (unitId) => {
    setFormData(prev => ({
      ...prev,
      unit_ids: prev.unit_ids.includes(unitId)
        ? prev.unit_ids.filter(id => id !== unitId)
        : [...prev.unit_ids, unitId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.pickup_number || !formData.vendor_id || !formData.driver_id) {
      toast.error('Please fill in required fields');
      return;
    }
    if (formData.unit_ids.length === 0) {
      toast.error('Please select at least one unit');
      return;
    }

    setLoading(true);
    try {
      const response = await pickupOrderAPI.create(formData);
      
      // Build order object for message preview
      const vendor = vendors.find(v => v.id === formData.vendor_id);
      const driver = drivers.find(d => d.id === formData.driver_id);
      const selectedUnits = units.filter(u => formData.unit_ids.includes(u.id));
      
      setCreatedOrder({
        ...response.data,
        vendor,
        driver_name: driver?.name,
        unit_numbers: selectedUnits.map(u => u.unit_number),
        parts_description: formData.parts_description,
        notes: formData.notes,
      });
      
      toast.success('Pickup order created!');
      onCreated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create pickup order');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setCreatedOrder(null);
    setFormData({
      pickup_number: '',
      vendor_id: '',
      driver_id: '',
      unit_ids: [],
      parts_description: '',
      notes: '',
    });
  };

  const handleCopyMessage = async () => {
    if (!createdOrder) return;
    const message = generateDriverMessage(createdOrder);
    try {
      await navigator.clipboard.writeText(message);
      toast.success('Message copied! Send to driver.');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const canCreate = ['system_admin', 'manager', 'purchasing'].includes(user?.role);

  if (!canCreate) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) handleClose();
      else setOpen(true);
    }}>
      <DialogTrigger asChild>
        <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-pickup-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Pickup Order
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        {createdOrder ? (
          /* Message Preview after creation */
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-5 h-5" />
                Pickup Order Created!
              </DialogTitle>
              <DialogDescription>
                Copy the message below and send it to {createdOrder.driver_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-sm whitespace-pre-wrap">
                {generateDriverMessage(createdOrder)}
              </div>
              <div className="flex gap-3">
                <Button onClick={handleCopyMessage} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="copy-created-message">
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Message for Driver
                </Button>
                <Button variant="outline" onClick={handleClose}>
                  Done
                </Button>
              </div>
            </div>
          </>
        ) : (
          /* Create Form */
          <>
            <DialogHeader>
              <DialogTitle>Create Pickup Order</DialogTitle>
              <DialogDescription>Create a new pickup order to send a driver to a vendor</DialogDescription>
            </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Pickup Number */}
          <div className="space-y-2">
            <Label>Pick Up Number *</Label>
            <Input
              value={formData.pickup_number}
              onChange={(e) => setFormData(prev => ({ ...prev, pickup_number: e.target.value.toUpperCase() }))}
              placeholder="PU-001"
              required
              data-testid="pickup-number-input"
            />
          </div>

          {/* Vendor Selection */}
          <div className="space-y-2">
            <Label>Vendor Location *</Label>
            <Select value={formData.vendor_id} onValueChange={(v) => setFormData(prev => ({ ...prev, vendor_id: v }))}>
              <SelectTrigger data-testid="vendor-select">
                <SelectValue placeholder="Select vendor location" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {vendors.map(v => (
                  <SelectItem key={v.id} value={v.id}>
                    <div className="flex flex-col">
                      <span className="font-medium">{v.name}</span>
                      <span className="text-xs text-slate-500">{v.address}, {v.city}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Driver Selection */}
          <div className="space-y-2">
            <Label>Driver *</Label>
            <Select value={formData.driver_id} onValueChange={(v) => setFormData(prev => ({ ...prev, driver_id: v }))}>
              <SelectTrigger data-testid="driver-select">
                <SelectValue placeholder="Select driver" />
              </SelectTrigger>
              <SelectContent>
                {drivers.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Units Selection */}
          <div className="space-y-2">
            <Label>Units (select multiple) *</Label>
            <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
              {units.length === 0 ? (
                <p className="text-sm text-slate-500">No units yet. Add one below.</p>
              ) : (
                units.map(unit => (
                  <div key={unit.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`unit-${unit.id}`}
                      checked={formData.unit_ids.includes(unit.id)}
                      onCheckedChange={() => toggleUnit(unit.id)}
                    />
                    <label htmlFor={`unit-${unit.id}`} className="text-sm font-medium cursor-pointer">
                      {unit.unit_number}
                      {unit.description && <span className="text-slate-500 ml-2">({unit.description})</span>}
                    </label>
                  </div>
                ))
              )}
            </div>
            
            {/* Add new unit */}
            <div className="flex gap-2">
              <Input
                value={newUnitNumber}
                onChange={(e) => setNewUnitNumber(e.target.value)}
                placeholder="Add new unit number"
                className="flex-1"
                data-testid="new-unit-input"
              />
              <Button type="button" variant="outline" onClick={handleAddUnit}>
                Add
              </Button>
            </div>
            
            {formData.unit_ids.length > 0 && (
              <p className="text-sm text-slate-600">
                Selected: {units.filter(u => formData.unit_ids.includes(u.id)).map(u => u.unit_number).join(', ')}
              </p>
            )}
          </div>

          {/* Parts Description */}
          <div className="space-y-2">
            <Label>Parts Description</Label>
            <Textarea
              value={formData.parts_description}
              onChange={(e) => setFormData(prev => ({ ...prev, parts_description: e.target.value }))}
              placeholder="What parts are being picked up?"
              rows={3}
              data-testid="parts-description"
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes..."
              rows={2}
              data-testid="pickup-notes"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-pickup-btn">
              {loading ? 'Creating...' : 'Create Pickup Order'}
            </Button>
          </div>
        </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function PickupOrdersPage() {
  const { user } = useAuthStore();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);

  const canEdit = ['system_admin', 'manager', 'purchasing'].includes(user?.role);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await pickupOrderAPI.getAll(params);
      setOrders(response.data.pickup_orders || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch pickup orders:', error);
      toast.error('Failed to load pickup orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const filteredOrders = orders.filter(order => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      order.pickup_number.toLowerCase().includes(query) ||
      order.vendor?.name?.toLowerCase().includes(query) ||
      order.driver_name?.toLowerCase().includes(query) ||
      order.unit_numbers?.some(u => u.toLowerCase().includes(query))
    );
  });

  const handleStatusUpdate = async (order, newStatus) => {
    try {
      await pickupOrderAPI.updateStatus(order.id, newStatus);
      toast.success(`Status updated to ${statusConfig[newStatus]?.label}`);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Stats
  const stats = {
    total: orders.length,
    created: orders.filter(o => o.status === 'created').length,
    inProgress: orders.filter(o => ['assigned', 'in_progress'].includes(o.status)).length,
    pickedUp: orders.filter(o => o.status === 'picked_up').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
  };

  return (
    <div className="space-y-6" data-testid="pickup-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pickup Orders</h1>
          <p className="text-slate-600">{total} total orders</p>
        </div>
        <CreatePickupDialog onCreated={fetchOrders} />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card className={cn("cursor-pointer", statusFilter === 'all' && "ring-2 ring-slate-900")} onClick={() => setStatusFilter('all')}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-slate-500">Total</p>
          </CardContent>
        </Card>
        <Card className={cn("cursor-pointer", statusFilter === 'created' && "ring-2 ring-slate-900")} onClick={() => setStatusFilter('created')}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-slate-600">{stats.created}</p>
            <p className="text-xs text-slate-500">Created</p>
          </CardContent>
        </Card>
        <Card className={cn("cursor-pointer", statusFilter === 'in_progress' && "ring-2 ring-slate-900")} onClick={() => setStatusFilter('in_progress')}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.inProgress}</p>
            <p className="text-xs text-slate-500">In Progress</p>
          </CardContent>
        </Card>
        <Card className={cn("cursor-pointer", statusFilter === 'picked_up' && "ring-2 ring-slate-900")} onClick={() => setStatusFilter('picked_up')}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.pickedUp}</p>
            <p className="text-xs text-slate-500">Picked Up</p>
          </CardContent>
        </Card>
        <Card className={cn("cursor-pointer", statusFilter === 'delivered' && "ring-2 ring-slate-900")} onClick={() => setStatusFilter('delivered')}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.delivered}</p>
            <p className="text-xs text-slate-500">Delivered</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by pickup #, vendor, driver, or unit..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-pickups"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-44" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="created">Created</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="picked_up">Picked Up</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-36 w-full" />
          ))}
        </div>
      ) : filteredOrders.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Truck className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No pickup orders found</h3>
            <p className="text-slate-500">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first pickup order'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <PickupOrderCard 
              key={order.id} 
              order={order} 
              onStatusUpdate={canEdit ? handleStatusUpdate : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}
